import { useRef } from 'react';
import { useSelector } from 'react-redux';

import type { Category } from '~/@types/redux';
import { useUser } from '~/common/hooks/useAuthentication';

import {
  getBrokerStatusLabel,
  getUnderwriterStatusLabel,
} from './useWorkTracking';

type EAction = 'Start' | 'Leave';

export type RailNavClickEvent = {
  buttonName: string;
};

export type NavSearchClickEvent = {
  buttonName: string;
  searchQuery: string;
};

export type MetaDataDyna = {
  category: string;
  description?: string;
  loanNumber?: string;
  role?: string;
  email?: string;
  parentItemTitle?: string;
  childItemTitle?: string;
  obfuscatedLoanRecordId?: number;
  action?: EAction;
  workItemStatus?: string;
  workItemCategoryStatus?: string;
};
// Mock dynatrace object for non-production environments
if (import.meta.env.DEV) {
  globalThis.dynatrace = {
    sendBizEvent: (eventName: string, eventData: object) => {
      /* eslint-disable no-console */
      console.group?.('sendBizEvent');
      console.log(eventName, eventData);
      console.groupEnd?.();
      /* eslint-enable no-console */
    },
  };
}

export const useDynaTracking = () => {
  const previousActionRef = useRef('');
  const previousMetaDataRef = useRef<MetaDataDyna | null>(null);
  const { navigationItems } = useSelector((s) => s.home);
  const user = useUser();

  // Helper: Find navigation item by category
  const findNavItemByCategory = (category: string) => {
    const categories = navigationItems.categories;
    if (!Array.isArray(categories)) return null;
    return categories.find(
      (item) =>
        item.navigationCategoryName?.toLowerCase() === category.toLowerCase(),
    );
  };

  // Helper: Get status label from navigation item and child title
  const getStatusLabelFromNavItem = (
    navItem: Category,
    childItemTitle: string,
  ) => {
    if (!Array.isArray(navItem?.navigationGutterItems)) return undefined;
    for (const item of navItem.navigationGutterItems) {
      const child = item.gutterItemChildren?.find(
        (child) =>
          child.displayTitle?.toLowerCase() === childItemTitle.toLowerCase(),
      );
      if (
        child ||
        item.displayTitle?.toLowerCase() === childItemTitle.toLowerCase()
      ) {
        const statusId = item.ruleResultStatusId;
        return user?.isUnderwriter
          ? getUnderwriterStatusLabel(statusId)
          : getBrokerStatusLabel(statusId);
      }
    }
    return undefined;
  };

  // Helper: Get classify status label for a category
  const getClassifyStatusLabel = (category: string) => {
    if (category.toLowerCase() !== 'classify') return undefined;
    const categories = navigationItems.categories;
    if (!Array.isArray(categories)) return undefined;
    const currentNavigationItem = categories.find(
      (item) =>
        item.navigationCategoryName?.toLowerCase() === category.toLowerCase(),
    );
    if (!currentNavigationItem) return undefined;
    if (currentNavigationItem.allRequiredRulesCompleted) {
      return user?.isUnderwriter ? 'Underwriter Verified' : 'Broker Verified';
    }
    return user?.isUnderwriter ? 'Underwriter Uncomplete' : 'Broker Uncomplete';
  };

  // Helper: Get status label for category and child title
  const getStatusLabel = (category?: string, childItemTitle?: string) => {
    if (!category || !childItemTitle) return undefined;
    const navItem = findNavItemByCategory(category);
    if (!navItem) return undefined;
    return getStatusLabelFromNavItem(navItem, childItemTitle);
  };

  // Helper: Get category status label (statusLabel or classifyStatusLabel)
  const getCategoryStatusLabel = (
    category?: string,
    childItemTitle?: string,
  ) => {
    if (!category || !childItemTitle) return undefined;
    const navItem = findNavItemByCategory(category);
    const statusLabel = getStatusLabelFromNavItem(navItem, childItemTitle);
    if (statusLabel) return statusLabel;
    const classifyStatusLabel = getClassifyStatusLabel(category);
    if (classifyStatusLabel) return classifyStatusLabel;
    return undefined;
  };

  const sendEnterAndLeaveActionDyna = (metaDataDyna: MetaDataDyna) => {
    const dynatrace = globalThis.dynatrace;
    if (!dynatrace) {
      // eslint-disable-next-line no-console
      console.error('Dynatrace object is not available');
      return;
    }
    const { action: currentAction, role } = metaDataDyna;
    const previousAction = previousActionRef.current;
    const previousMetaData = previousMetaDataRef.current;

    const ruleStatusLabel1 = getStatusLabel(
      metaDataDyna.category,
      metaDataDyna.childItemTitle,
    );
    const ruleStatusLabel2 = getCategoryStatusLabel(
      metaDataDyna.category,
      metaDataDyna.childItemTitle,
    );
    if (previousAction && currentAction !== previousAction) {
      const prevStatusLabel = getStatusLabel(
        previousMetaData?.category,
        previousMetaData?.childItemTitle,
      );
      const prevCategoryStatusLabel = getCategoryStatusLabel(
        previousMetaData?.category,
        previousMetaData?.childItemTitle,
      );
      const leavingMetaData = {
        ...previousMetaData,
        action: 'Leave',
        workItemStatus: prevStatusLabel,
        workItemCategoryStatus: `${previousMetaData.category} | ${previousMetaData.parentItemTitle} - ${prevCategoryStatusLabel} | ${previousMetaData.childItemTitle} - ${prevStatusLabel}`,
      } as MetaDataDyna;
      dynatrace.sendBizEvent(`${role}-Tracking`, leavingMetaData);
    }
    const startingMetaData = {
      ...metaDataDyna,
      action: 'Start',
      workItemStatus: ruleStatusLabel1,
      workItemCategoryStatus: `${metaDataDyna.category} | ${metaDataDyna.parentItemTitle} - ${ruleStatusLabel2} | ${metaDataDyna.childItemTitle} - ${ruleStatusLabel1}`,
    } as MetaDataDyna;
    dynatrace.sendBizEvent(`${role}-Tracking`, startingMetaData);

    previousActionRef.current = currentAction;
    previousMetaDataRef.current = metaDataDyna;
  };
  const sendRailNavClickEvent = (event: RailNavClickEvent) => {
    const dynatrace = globalThis.dynatrace;
    if (!dynatrace) return;
    dynatrace.sendBizEvent('RailNav-Click', {
      ...event,
      role: user?.role ?? 'Unknown',
      email: user?.email,
    });
  };

  const sendNavSearchClickEvent = (event: NavSearchClickEvent) => {
    const dynatrace = globalThis.dynatrace;
    if (!dynatrace) return;
    dynatrace.sendBizEvent('NavSearch-Click', {
      ...event,
      role: user?.role ?? 'Unknown',
      email: user?.email,
    });
  };

  return {
    sendEnterAndLeaveActionDyna,
    sendRailNavClickEvent,
    sendNavSearchClickEvent,
  };
};
